import { Component, Prop, Host, h, State, Element } from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import { WindowResizeService } from '../../services/window-resize-service';
import { ResponsiveString } from '../../typings';
import { createFocusTrap } from 'focus-trap';

/**
 * @slot openButton - This slot will expand the navigation when clicked.
 * @slot closeButton - This slot will close the navigation when clicked.
 * @slot skipContent - This slot is a keyboard-focus link that lets a screen-reader or keyboard user skip the navigation block and scroll to the end.
 * @slot content - Default slot
 */
@Component({
  tag: 'content-navigation-block',
  styleUrl: 'content-navigation-block.scss',
  shadow: false,
})
export class ContentNavigationBlock {
  /**
   * @type Select
   * @responsive
   * @options fixed
   * @options absolute
   * @options sticky
   * @options static
   * @options none
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  hamburger: ResponsiveString = 'fixed|none';

  /**
   * @type String
   * @responsive
   */
  @Prop({
    mutable: true,
    reflect: true,
  })
  classes: ResponsiveString;
  @State() classString;
  @Element() hostElement: HTMLStencilElement;
  @State() ariaExpanded: string = 'false';
  @State() bodyToggle: boolean = false;
  @State() skipAccessibilityMessage: HTMLElement;

  @State() hasOpenButton: boolean = false;
  @State() hasCloseButton: boolean = false;

  @State() openButton: HTMLElement;
  @State() closeButton: HTMLElement;
  @State() contentSlot: HTMLElement;
  focusTrap;
  componentWillLoad() {
    this.hasOpenButton = !!this.hostElement.querySelector('[slot="openButton"]');
    this.hasCloseButton = !!this.hostElement.querySelector('[slot="closeButton"]');
    WindowResizeService.registerProp(this.hamburger).subscribe(newValue => {
      this.hamburger = newValue as string;
      this.bodyToggle = this.hamburger === 'fixed';
      document.body.classList.remove('content-navigation-block-open');
    });
    WindowResizeService.registerProp(this.classes).subscribe(newValue => {
      this.classString = newValue as string;
    });
  }

  componentDidLoad() {
    this.openButton.onclick = () => {
      this.openMenu();
    };
    this.closeButton.onclick = () => {
      this.closeMenu();
    };
  }

  openMenu() {
    this.ariaExpanded = this.ariaExpanded === 'true' ? 'false' : 'true';
    if (this.ariaExpanded === 'false') {
      document.body.classList.remove('content-navigation-block-open');
    } else if (this.bodyToggle) {
      document.body.classList.add('content-navigation-block-open');
    }
    this.focusTrap = createFocusTrap(this.contentSlot.parentElement, {
      initialFocus: this.contentSlot,
      fallbackFocus: this.hostElement,
      clickOutsideDeactivates: true,
      onDeactivate: () => {
        this.closeMenu();
        this.focusTrap = null;
        this.ariaExpanded = 'false';
        document.body.classList.remove('content-navigation-block-open');
        setTimeout(() => this.openButton.focus(), 1);
      },
    });

    this.focusTrap.activate();
  }
  closeMenu() {
    if (this.focusTrap) {
      try {
        this.focusTrap.deactivate();
      } catch (e) {
        console.log(e);
      }
    }
  }

  render() {
    return (
      <Host class={this.classString} aria-expanded={this.ariaExpanded}>
        <div class="content-navigation-block__skip-content">
          <slot name="skipContent"></slot>
        </div>
        <div ref={el => (this.openButton = el)} class="content-navigation__open">
          {!this.hasOpenButton && (
            <button>
              <svg class="default-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          )}
          <slot name="openButton"></slot>
        </div>
        <div class="content-navigation__content-wrapper">
          <div ref={el => (this.closeButton = el)} class="content-navigation__close">
            {!this.hasCloseButton && (
              <button>
                <svg class="default-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <slot name="closeButton"></slot>
          </div>
          <div class="content-navigation__content" ref={el => (this.contentSlot = el)}>
            <slot name="content"></slot>
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
